import React, { ReactElement, useEffect, useState } from 'react'
import { Box, IconButton, Theme } from '@mui/material'
import { AnimatePresence } from 'framer-motion'
import { useIntl } from 'react-intl'
import { navigate } from 'gatsby'

import Popup from '@components/core/container/Popup'
import Headline from '@components/core/text/Headline'
import Copy from '@components/core/text/Copy'
import Icon from '@components/core/media/Icon'
import AutocompleteInput from '@components/core/form/fields/AutocompleteInput'
import useQueryParams from '@components/site/hooks/useQueryParams'
import useSearchState from '@components/site/hooks/search/useSearchState'
import { searchFranchiseeOptions } from '@utils/franchisee/franchisee'
import { lightboxDialogMotionProps, StyledMotionDiv } from '@utils/motion'
import useGlobalState from '@components/site/hooks/useGlobalState'

export type FranchiseeSearchOverlayProps = {
  isOpen: boolean
  onClose: () => void
}

export default function FranchiseeSearchOverlay({
  isOpen,
  onClose,
}: FranchiseeSearchOverlayProps): ReactElement | null {
  const intl = useIntl()
  const searchState = useSearchState()
  const globalState = useGlobalState()
  const { setFranchiseeId, setFranchiseMode } = useQueryParams()
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState<MCDC.Props.IOption[]>([])
  const [isBusy, setIsBusy] = useState(false)

  useEffect(() => {
    if (!query.trim()) {
      setOptions([])
      setIsBusy(false)
      return
    }

    let cancelled = false
    const timeoutId = window.setTimeout(() => {
      setIsBusy(true)
      searchFranchiseeOptions(query).then((results) => {
        if (cancelled) return
        setOptions(results)
        setIsBusy(false)
      })
    }, 300)

    return () => {
      cancelled = true
      window.clearTimeout(timeoutId)
    }
  }, [query])

  useEffect(() => {
    if (!isOpen) return

    document.body.classList.add('Mui-noScroll')
    document.body.setAttribute(
      'style',
      `padding-right: ${
        window.innerWidth - document.documentElement.clientWidth
      }px`
    )

    return () => {
      document.body.classList.remove('Mui-noScroll')
      document.body.removeAttribute('style')
    }
  }, [isOpen])

  function handleSelect(option: MCDC.Props.IOption | string | null) {
    if (!option || typeof option === 'string') return

    const offersUrl = searchState.linkTo?.url || '/'
    const isOnOffersPage =
      globalState.location?.pathname.includes(offersUrl) ?? false

    setFranchiseMode(false)
    setFranchiseeId(String(option.value))

    if (!isOnOffersPage) {
      navigate(
        `${offersUrl}?${new URLSearchParams({
          franchiseeId: String(option.value),
        }).toString()}`
      )
      return
    }

    onClose()
  }

  if (!isOpen) return null

  return (
    <Box
      sx={(theme: Theme) => ({
        position: 'fixed',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        zIndex: theme.zIndex.modal,
      })}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          width: '100%',
          height: '100%',
        }}
      >
        <Box
          onClick={onClose}
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            cursor: 'pointer',
          }}
        />
        <Box
          sx={(theme: Theme) => ({
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            overflow: 'auto',
            p: theme.gutter('sm'),
          })}
        >
          <AnimatePresence>
            <StyledMotionDiv
              {...lightboxDialogMotionProps}
              sx={{ width: '100%', maxWidth: 539, zIndex: 1 }}
            >
              <Popup
                sx={{
                  maxWidth: '100%',
                  p: 6,
                  border: '1px solid',
                  borderColor: '#8c8c8d',
                  color: 'text.primary',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: 1,
                    width: '100%',
                  }}
                >
                  <IconButton
                    onClick={onClose}
                    color="inherit"
                    aria-label={intl.formatMessage({ id: 'label.menuClose' })}
                    sx={{ p: 0, width: 24, height: 24 }}
                  >
                    <Icon variant="close" />
                  </IconButton>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 2,
                      width: '100%',
                      pb: 2,
                    }}
                  >
                    <Headline variant="h5" sx={{ width: '100%' }}>
                      {intl.formatMessage({
                        id: 'franchisee.overlay.headline',
                      })}
                    </Headline>
                    <Copy sx={{ width: '100%' }}>
                      {intl.formatMessage({ id: 'franchisee.overlay.copy' })}
                    </Copy>
                    <AutocompleteInput
                      id="franchiseeSearch"
                      icon="search"
                      value={null}
                      inputValue={query}
                      options={options}
                      label={intl.formatMessage({
                        id: 'franchisee.overlay.placeholder',
                      })}
                      isClearable
                      isOpenOnFocus
                      isLoading={isBusy}
                      onChange={(e, value) => handleSelect(value)}
                      onInputChange={(e, value) => setQuery(value)}
                      sx={{
                        width: '100%',
                        '& .MuiAutocomplete-popper': {
                          boxShadow: '-1px 2px 4px rgba(0, 0, 0, 0.11)',
                        },
                        '& .MuiAutocomplete-option[aria-selected="true"]': {
                          backgroundColor: 'rgba(255, 188, 13, 0.2) !important',
                        },
                        '& .MuiAutocomplete-option:hover': {
                          backgroundColor: 'rgba(255, 188, 13, 0.2)',
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Popup>
            </StyledMotionDiv>
          </AnimatePresence>
        </Box>
      </Box>
    </Box>
  )
}
